<template>
  <v-row no-gutters class="chat-widget">
    <v-col cols="auto" class="ai-response-bubble" :style="`background: ${config.response_color}; color: ${config.response_text_color}`">
      <!-- Answer by -->
      <div class="d-flex" style="justify-content: space-between">
        <div v-if="message.generated_by">
          <v-tooltip location="top" content-class="tooltip-top tooltip-color mrc-tooltip" max-width="210px" aria-label="streaming-chat-ai-answer-by-tooltip">
            <template v-slot:activator="{ props }">
              <v-chip outlined small class="mrc-chip" v-bind="props"> <v-icon color="#828282">mdi-creation</v-icon>{{ i18n_answer_by }}</v-chip>
            </template>
            <span>{{ i18n_answer_desc }}</span>
          </v-tooltip>
        </div>
        <div v-if="message.is_complete === false">
          <v-progress-circular class="loading-circular" color="#e22925" indeterminate aria-label="streaming-chat-user-progress" />
        </div>
      </div>

      <!-- Text -->
      <span style="word-break: break-word" v-html="messageText"></span>

      <!-- Ref -->
      <div style="padding-top: 1rem">
        <div v-if="message.ref_url">
          <span class="hint-text d-flex" style="align-items: center" v-for="(url, url_index) in message.ref_url" :key="url_index">
            <v-icon style="font-size: 1rem !important; margin-right: 0.25rem" color="#0098FD">mdi-information</v-icon>
            <a :href="url" target="_blank" class="ellipsis-text" style="color: #0098fd">{{ url }}</a>
          </span>
        </div>

        <div v-if="message.ref_urls">
          <span class="hint-text d-flex" style="align-items: center" v-for="(ref, ref_index) in message.ref_urls" :key="ref_index">
            <v-tooltip location="top" content-class="tooltip-top tooltip-color mrc-tooltip" max-width="210px" aria-label="streaming-chat-ai-ref-tooltip">
              <template v-slot:activator="{ props }">
                <v-icon style="font-size: 1rem !important; margin-right: 0.25rem" color="#0098FD" v-bind="props">mdi-information</v-icon>
                <a :href="ref.url" target="_blank" class="ellipsis-text" style="color: #0098fd" v-bind="props">{{ ref.title }}</a>
              </template>
              <span>{{ ref.content }}</span>
            </v-tooltip>
          </span>
        </div>
      </div>

      <!-- Remarks -->
      <div v-if="message.remarks">
        <span class="hint-text" style="margin-top: 0.25rem; color: #828282">{{ message.remarks }}</span>
      </div>
    </v-col>

    <!-- Bottom prompt bar -->
    <v-col cols="12" v-if="message.is_allow_user_rating">
      <PromptBar :is_last_message="is_last_message" />
    </v-col>
  </v-row>
</template>

<script setup>
// Import modules
import global_config from '@/../config.json'
import PromptBar from '../Widgets/PromptBar'
import { ref, defineProps, computed, watch, onMounted } from 'vue'
import { useLocale } from 'vuetify'

// Init modules
const { t } = useLocale()

// Data
const config = ref(global_config)
const text = ref(message.text)
const streaming_text = ref('')

// Props
const { message } = defineProps({
  message: Object,
  is_last_message: Boolean
})

const messageText = computed(() => {
  let text = streaming_text.value
  let result = ''

  var boldRegex = /(\*\*.*?\*\*)/g
  var urlRegex = /((https?|ftp|sftp):\/\/[^\s]+(?=(<[^>]*>)))|((https?|ftp|sftp):\/\/[^\s]+)/g

  result = text
    .replace(boldRegex, (boldText) => {
      return '<b>' + boldText.slice(2, -2) + '</b>'
    })
    .replace(urlRegex, (url) => {
      return '<a href="' + url + '"  target="_blank">' + url + '</a>'
    })

  return result
})

const i18n_answer_by = computed(() => {
  if (message.generated_by === 'MRC') return t('$vuetify.__ChatBubbleAi.answer_by_mrc')
  else if (message.generated_by === 'ChatGPT') return t('$vuetify.__ChatBubbleAi.answer_by_chatGPT')
  else return ''
})

const i18n_answer_desc = computed(() => {
  if (message.generated_by === 'MRC') return t('$vuetify.__ChatBubbleAi.mrc_answer_desc')
  else if (message.generated_by === 'ChatGPT') return t('$vuetify.__ChatBubbleAi.chatgpt_answer_desc')
  else return ''
})

// Methods
const append_text = () => {
  let random_time = Math.floor(Math.random() * 46) + 5

  setTimeout(() => {
    if (streaming_text.value.length < text.value.length) {
      if (text.value[streaming_text.value.length] === '<') {
        const remaining_text = text.value.slice(streaming_text.value.length)
        const first_arrow_index = remaining_text.indexOf('>')
        const next_slash_index = remaining_text.indexOf('/')
        const second_arrow_index = remaining_text.indexOf('>', first_arrow_index + 1)

        if (next_slash_index > 0 && next_slash_index < second_arrow_index) streaming_text.value = streaming_text.value.concat(remaining_text.slice(0, second_arrow_index + 1))
        else streaming_text.value = streaming_text.value.concat(remaining_text.slice(0, first_arrow_index + 1))
      } else streaming_text.value = streaming_text.value.concat(text.value[streaming_text.value.length])
      append_text()
    }
  }, random_time)
}

watch(
  () => message.text,
  () => {
    text.value = message.text
  }
)

watch(text, () => {
  append_text()
})

onMounted(() => {
  append_text()
})
</script>

<style scope>
.mrc-tooltip {
  max-width: 19.0625rem;
}
.mrc-chip {
  border: 1px solid rgba(0, 0, 0, 0.2);
  max-height: 1.5rem;
  font-weight: 400;
  font-size: 0.75rem !important;
  line-height: 1rem;
  letter-spacing: 0.01875rem;
  color: #1b1b1b;
  margin-bottom: 1rem;
}

.v-chip .v-icon {
  font-size: 1rem !important;
  margin-right: 0.5rem;
}

.hint-text {
  width: 100%;
  display: block;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  letter-spacing: 0.01875rem;
}

.thumbs-button {
  font-size: 1.125rem !important;
}

.loading-circular {
  height: 1.25rem !important;
  width: 1.25rem !important;
  margin-left: 0.5rem;
}
</style>
